import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect } from "react";
import styled, { useTheme } from "styled-components";
import mixpanel from "mixpanel-browser";
import { Text, Button, Card, Grid, Icon, Alert, InputText, Picture, Box } from "~/components-shared";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import AddCommunitiesForm from "~/forms/AddCommunitiesForm";
import addCommunitiesSchema from "~/forms/AddCommunitiesForm/addCommunitiesSchema";
import { useDispatch, useSelector } from "react-redux";
import onboardingActionCreators from "~/store/onboarding/actions";
import authSelectors from "~/store/auth/selectors";
import { helpers, onboardingOrder } from "~/lib/helpers";
import { MemberStatusEnum, StatusStateEnum } from "~/types/enums";
import onboardingSelectors from "~/store/onboarding/selectors";
import communitySelectors from "~/store/communities/selectors";
import communitiesActionCreators from "~/store/communities/actions";
const Form = styled.form`
  width: 100%;
`;
const StyledIcon = styled(Icon)`
  box-shadow: inset 0 0 0 6px ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.success.main;
}};
  border-radius: 50%;
`;
const AddCommunitiesCard = _ref2 => {
  let {
    open,
    ...rest
  } = _ref2;
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const user = useSelector(authSelectors.getUser);
  const communities = useSelector(communitySelectors.getCommunities);
  const communityStatus = useSelector(communitySelectors.getStatus);
  const dispatch = useDispatch();
  const success = user?.status && onboardingOrder[user.status] > onboardingOrder[MemberStatusEnum.ONBOARDING_ADD_COMMUNITIES];
  const nextStatus = useSelector(onboardingSelectors.getStatus);
  const backStatus = useSelector(onboardingSelectors.getBackStatus);
  const nextError = useSelector(onboardingSelectors.getError);
  const backError = useSelector(onboardingSelectors.getBackError);
  const availableStatuses = helpers.memberOnboardingStatuses(user.authorities);
  const statusIndex = availableStatuses.findIndex(item => item === user.status);
  const error = nextError || backError;
  const {
    handleSubmit,
    control,
    setError,
    formState: {
      errors
    }
  } = useForm({
    resolver: yupResolver(addCommunitiesSchema),
    defaultValues: {
      communities: Array(3).fill(1).map(_e => ({
        name: ""
      }))
    }
  });
  useEffect(() => {
    if (communityStatus === StatusStateEnum.IDLE) {
      dispatch(communitiesActionCreators.fetchStarted());
    }
  }, [communityStatus]);
  const onBack = () => {
    dispatch(onboardingActionCreators.backStarted());
    mixpanel.track(`Onboarding Add Communities – Back`);
  };
  const onSubmit = handleSubmit(data => {
    const validCommunities = {
      communities: data.communities.filter(community => community.name)
    };
    if (!validCommunities.communities.length) {
      setError("communities", {
        message: t("AddCommunitiesCard.required")
      });
      return;
    }
    dispatch(onboardingActionCreators.addCommunitiesSubmitStarted(validCommunities));
  });
  const onSkip = () => {
    dispatch(onboardingActionCreators.addCommunitiesSkipStarted());
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      px: 10,
      py: 6,
      mb: 10
    },
    collapsed: !open,
    elemAfter: success ? /*#__PURE__*/React.createElement(StyledIcon, {
      src: "checkboxFill",
      scale: 8,
      color: theme.colors.success.main
    }) : undefined,
    shadow: true,
    align: "left"
  }, rest), /*#__PURE__*/React.createElement(Text, {
    element: "h6",
    weight: "regular",
    s: {
      mt: 2,
      mb: 5
    }
  }, t("AddCommunitiesCard.howMany")), /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, t("AddCommunitiesCard.existingCommunities")), /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    gap: 3,
    fullWidth: true,
    s: {
      mb: 6
    },
    equal: true
  }, communities.map(community => /*#__PURE__*/React.createElement(InputText, {
    key: community.id,
    value: community.name,
    elemBefore: /*#__PURE__*/React.createElement(Picture, {
      width: "34px",
      height: "34px",
      name: community.name,
      src: community.picture,
      color: community.color || theme.colors.secondary.main,
      s: {
        mr: 2
      }
    }),
    elemAfter: /*#__PURE__*/React.createElement(Text, {
      element: "span",
      color: theme.colors.text.secondary,
      weight: "regular",
      fontSize: 3.5,
      s: {
        mr: 3
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "people",
      scale: 3
    }), " ", community.memberIds.length),
    disabled: true
  }))), /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, t("AddCommunitiesCard.addCommunities")), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), errors?.communities?.message && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, errors.communities.message), /*#__PURE__*/React.createElement(AddCommunitiesForm, {
    control: control,
    name: "communities"
  }), /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    gap: 5,
    justify: "end",
    s: {
      mt: 10,
      mb: 2
    },
    fullWidth: true
  }, statusIndex !== 0 && /*#__PURE__*/React.createElement(Button, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "arrow",
      scale: 5,
      rotate: 180,
      s: {
        mr: 2
      }
    }),
    color: "translucent",
    type: "submit",
    onClick: onBack,
    loading: backStatus === StatusStateEnum.LOADING,
    s: {
      pl: 4,
      pr: 6
    },
    rounded: true
  }, t("common.actions.back")), /*#__PURE__*/React.createElement(Button, {
    onClick: onSkip,
    color: "translucent",
    s: {
      px: 8
    },
    rounded: true
  }, t("common.actions.skip")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    type: "submit",
    maxWidth: "230px",
    onClick: onSubmit,
    loading: nextStatus === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("common.actions.save")))));
};
export default AddCommunitiesCard;